import { useState } from "react";
import { LineChart, Line, BarChart, Bar, AreaChart, Area, RadarChart, Radar, PolarGrid, PolarAngleAxis, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, PolarRadiusAxis } from "recharts";

const weeklyData = [
  { day: "Mon", sessions: 4200, bounce: 42, conversions: 180 },
  { day: "Tue", sessions: 5800, bounce: 38, conversions: 240 },
  { day: "Wed", sessions: 5100, bounce: 44, conversions: 210 },
  { day: "Thu", sessions: 7300, bounce: 35, conversions: 310 },
  { day: "Fri", sessions: 6800, bounce: 37, conversions: 290 },
  { day: "Sat", sessions: 4500, bounce: 50, conversions: 155 },
  { day: "Sun", sessions: 3200, bounce: 55, conversions: 110 },
];

const trafficData = [
  { source: "Organic", value: 42, color: "#6EE7B7" },
  { source: "Direct", value: 28, color: "#60A5FA" },
  { source: "Social", value: 18, color: "#A78BFA" },
  { source: "Referral", value: 12, color: "#F472B6" },
];

const radarData = [
  { metric: "Performance", A: 85, B: 70 }, { metric: "Engagement", A: 92, B: 75 },
  { metric: "Retention", A: 78, B: 82 }, { metric: "Revenue", A: 88, B: 65 },
  { metric: "Support", A: 70, B: 88 }, { metric: "Growth", A: 95, B: 72 },
];

const funnelData = [
  { stage: "Visitors", count: 100000, color: "#6EE7B7" },
  { stage: "Leads", count: 42000, color: "#60A5FA" },
  { stage: "Qualified", count: 18000, color: "#A78BFA" },
  { stage: "Converted", count: 7200, color: "#F472B6" },
  { stage: "Retained", count: 5800, color: "#FBBF24" },
];

const METRICS = [
  { label: "Avg Session", value: "4m 32s", change: "+0:24", up: true, accent: "#6EE7B7" },
  { label: "Bounce Rate", value: "38.5%", change: "-3.2%", up: true, accent: "#60A5FA" },
  { label: "Conv. Rate", value: "3.8%", change: "+0.6%", up: true, accent: "#A78BFA" },
  { label: "Page Views", value: "2.4M", change: "+22%", up: true, accent: "#F472B6" },
];

function Tip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div style={{ background: "rgba(13,17,28,0.97)", border: "1px solid rgba(110,231,183,0.15)", borderRadius: 10, padding: "10px 16px" }}>
      <p style={{ color: "#8892a4", fontSize: 12, marginBottom: 6 }}>{label}</p>
      {payload.map((p, i) => <p key={i} style={{ color: p.color, fontSize: 13, fontWeight: 700, margin: "2px 0" }}>{p.value.toLocaleString()} <span style={{ color: "#8892a4", fontWeight: 400, fontSize: 11 }}>{p.dataKey}</span></p>)}
    </div>
  );
}

export default function Analytics() {
  const [activeTab, setActiveTab] = useState("overview");
  const tabs = ["overview", "traffic", "conversions", "reports"];

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ fontSize: 22, fontWeight: 800, color: "#E8EDF5", marginBottom: 4 }}>Analytics</h1>
        <p style={{ fontSize: 13, color: "#8892a4" }}>Deep dive into your performance metrics</p>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 24, background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12, padding: 4, width: "fit-content" }}>
        {tabs.map(tab => (
          <button key={tab} onClick={() => setActiveTab(tab)}
            style={{ padding: "8px 18px", borderRadius: 9, border: "none", cursor: "pointer", fontSize: 13, fontWeight: 600, fontFamily: "inherit", textTransform: "capitalize", transition: "all 0.2s", background: activeTab === tab ? "rgba(110,231,183,0.12)" : "transparent", color: activeTab === tab ? "#6EE7B7" : "#8892a4" }}
            onMouseEnter={e => { if (activeTab !== tab) e.currentTarget.style.color = "#E8EDF5"; }}
            onMouseLeave={e => { if (activeTab !== tab) e.currentTarget.style.color = "#8892a4"; }}>
            {tab}
          </button>
        ))}
      </div>

      {/* Metric cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 14, marginBottom: 20 }}>
        {METRICS.map((m, i) => (
          <div key={i} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 14, padding: "18px 20px", transition: "all 0.2s", cursor: "pointer" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = `${m.accent}30`; e.currentTarget.style.boxShadow = `0 0 0 1px ${m.accent}20`; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"; e.currentTarget.style.boxShadow = "none"; }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: "#4B5563", letterSpacing: "0.08em", marginBottom: 10 }}>{m.label.toUpperCase()}</p>
            <p style={{ fontSize: 24, fontWeight: 800, color: "#E8EDF5", letterSpacing: "-0.02em", marginBottom: 6 }}>{m.value}</p>
            <span style={{ fontSize: 12, fontWeight: 600, color: m.up ? "#6EE7B7" : "#F87171" }}>{m.up ? "▲" : "▼"} {m.change} vs last period</span>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 14 }}>
        <Box title="Weekly Sessions & Conversions">
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={weeklyData}>
              <defs>
                <linearGradient id="sg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6EE7B7" stopOpacity={0.2} /><stop offset="95%" stopColor="#6EE7B7" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="cg" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#A78BFA" stopOpacity={0.2} /><stop offset="95%" stopColor="#A78BFA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#8892a4" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: "#8892a4" }} axisLine={false} tickLine={false} />
              <Tooltip content={<Tip />} />
              <Legend wrapperStyle={{ fontSize: 11, color: "#8892a4" }} />
              <Area type="monotone" dataKey="sessions" stroke="#6EE7B7" strokeWidth={2} fill="url(#sg)" dot={false} />
              <Area type="monotone" dataKey="conversions" stroke="#A78BFA" strokeWidth={2} fill="url(#cg)" dot={false} />
            </AreaChart>
          </ResponsiveContainer>
        </Box>

        <Box title="Performance Radar">
          <ResponsiveContainer width="100%" height={220}>
            <RadarChart data={radarData}>
              <PolarGrid stroke="rgba(255,255,255,0.07)" />
              <PolarAngleAxis dataKey="metric" tick={{ fontSize: 11, fill: "#8892a4" }} />
              <PolarRadiusAxis tick={{ fontSize: 9, fill: "#4B5563" }} />
              <Radar name="This Period" dataKey="A" stroke="#6EE7B7" fill="#6EE7B7" fillOpacity={0.15} strokeWidth={2} />
              <Radar name="Last Period" dataKey="B" stroke="#60A5FA" fill="#60A5FA" fillOpacity={0.1} strokeWidth={1.5} strokeDasharray="4 4" />
              <Legend wrapperStyle={{ fontSize: 11, color: "#8892a4" }} />
            </RadarChart>
          </ResponsiveContainer>
        </Box>
      </div>

      {/* Traffic sources + Funnel */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
        <Box title="Traffic Sources" sub="Where your visitors come from">
          <div style={{ display: "flex", flexDirection: "column", gap: 14, marginTop: 8 }}>
            {trafficData.map((t, i) => (
              <div key={i}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: t.color }} />
                    <span style={{ fontSize: 13, color: "#C8D0DC", fontWeight: 500 }}>{t.source}</span>
                  </div>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#E8EDF5" }}>{t.value}%</span>
                </div>
                <div style={{ height: 6, background: "rgba(255,255,255,0.06)", borderRadius: 10, overflow: "hidden" }}>
                  <div style={{ height: "100%", width: `${t.value}%`, background: t.color, borderRadius: 10, transition: "width 1s ease" }} />
                </div>
              </div>
            ))}
          </div>
        </Box>

        <Box title="Conversion Funnel" sub="User journey stages">
          <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
            {funnelData.map((f, i) => {
              const pct = Math.round((f.count / funnelData[0].count) * 100);
              return (
                <div key={i} style={{ position: "relative" }}>
                  <div style={{ height: 38, background: `${f.color}12`, border: `1px solid ${f.color}20`, borderRadius: 10, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 14px" }}>
                    <div style={{ position: "absolute", left: 0, top: 0, height: "100%", width: `${pct}%`, background: `${f.color}08`, borderRadius: 10 }} />
                    <span style={{ fontSize: 13, fontWeight: 600, color: "#E8EDF5", position: "relative" }}>{f.stage}</span>
                    <div style={{ display: "flex", gap: 12, position: "relative" }}>
                      <span style={{ fontSize: 12, color: "#8892a4" }}>{f.count.toLocaleString()}</span>
                      <span style={{ fontSize: 12, fontWeight: 700, color: f.color }}>{pct}%</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </Box>
      </div>
    </div>
  );
}

function Box({ title, sub, children }) {
  return (
    <div style={{ background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.07)", borderRadius: 16, padding: "20px", transition: "border-color 0.2s" }}
      onMouseEnter={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.12)"}
      onMouseLeave={e => e.currentTarget.style.borderColor = "rgba(255,255,255,0.07)"}>
      <h3 style={{ fontSize: 14, fontWeight: 800, color: "#E8EDF5", margin: 0 }}>{title}</h3>
      {sub && <p style={{ fontSize: 12, color: "#8892a4", margin: "3px 0 0" }}>{sub}</p>}
      {children}
    </div>
  );
}